import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseService } from '../../shared/services/base.service';
import { AppService } from '../../shared/services/app.service';

const CONFIG_QUERY_ID = 'GET_CONFIG_FOR_ACCOUNT';
const CONFIG_ARGS = 'module_code:account-management,config_code:third-party-patient-bot';
const CONFIG_RESOURCE = 'facility_config_mapping';
const CONFIG_APPLICATION = 'facility-management';
const AUTH_CODE_RESOURCE = 'chatbot_auth_code';
const AUTH_CODE_APPLICATION = 'serviceworkflow';

/**
 * Floating chatbot launcher, fixed bottom-right on every page — patient users
 * only. Visibility and the embedded base URL are config-driven: on init (for
 * patient sessions only) it fetches the `third-party-patient-bot` account
 * config, and only renders the icon when a usable URL comes back (config
 * missing, empty, "No", or status "in-active" -> stays hidden).
 * Clicking the icon requests a one-time authorization code from
 * kx-serviceworkflow (bound to this KX session + the open patient's mrNumber)
 * and only then opens a panel embedding `<configured URL>?code=<code>` in an
 * iframe — the JWT itself never reaches the browser/iframe URL; TSL's server
 * exchanges the code for it directly with kx-serviceworkflow. The header's
 * close button hides the panel again without destroying the icon.
 *
 * Standalone widget — independent of ChatbotComponent (base/components/chatbot,
 * unused) and KxAiChatEmbedComponent (the live AI assistant embed), which
 * solve different problems.
 */
@Component({
    selector: 'app-chatbot-widget',
    standalone: false,
    templateUrl: './chatbot-widget.component.html',
    styleUrls: ['./chatbot-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotWidgetComponent implements OnInit, OnDestroy {
  visible = false;
  chatUrl: string | null = null;
  authError = false;
  authorizing = false;

  isOpen = false;
  // Iframe is only ever added to the DOM once (on first open, after the
  // authorization code comes back) so the panel can be hidden/shown via CSS
  // afterwards instead of destroying/re-fetching a code and reloading the
  // iframe on every toggle.
  loaded = false;

  private chatBaseUrl: string | null = null;
  private safeChatUrl: SafeResourceUrl | null = null;
  private safeChatUrlFor: string | null = null;
  private routerSubscription: Subscription | undefined;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly baseService: BaseService,
    private readonly appService: AppService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Patient-facing widget only — staff/account/system users never see it,
    // and never trigger the config fetch either.
    if (this.baseService.getUserType() !== 'patient') {
      return;
    }

    // 202174: this widget lives at the app-shell level behind the isLoggedIn gate (see
    // app.component.html), which mounts it the moment login-service calls setUser() — BEFORE
    // the GET_MY_CONFIG round-trip that populates _currentSessionData.encryptKey. Firing the
    // config fetch straight away in that window signs its X-XSRF-TOKEN with a plain MD5 instead
    // of the encrypted one, and the gateway rejects it as "BAD RESPONSE". kx-ai-chat-embed hit
    // the identical race (see its isSessionConfigReady()) — same fix here: fire immediately if
    // config is already loaded (page reload with an existing session), otherwise wait for a
    // completed navigation. Unlike the password-login flow, quick-OTP login navigates straight
    // to a report-download route WITHOUT ever calling parseMyConfigObject(), so the first
    // NavigationEnd is not a reliable readiness signal by itself — re-check isSessionConfigReady()
    // on every NavigationEnd (mirroring kx-ai-chat-embed's tryInitializeChat()) instead of
    // trusting the first one blindly.
    if (this.isSessionConfigReady()) {
      this.fetchBotConfig();
    } else {
      this.routerSubscription = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        if (!this.isSessionConfigReady()) {
          return;
        }
        this.routerSubscription?.unsubscribe();
        this.routerSubscription = undefined;
        this.fetchBotConfig();
      });
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  private isSessionConfigReady(): boolean {
    return !!this.appService?._currentSessionData?.configLoaded;
  }

  private fetchBotConfig(): void {
    this.baseService.get(CONFIG_QUERY_ID, CONFIG_ARGS, CONFIG_RESOURCE, CONFIG_APPLICATION).subscribe({
      next: (res: any) => {
        const record = res?.errCode === 0 ? res?.resource?.[0] : undefined;
        // Live response shape uses `text_value` (result_type: "textbox"), not
        // `config_value` — verified against the actual GET_CONFIG_FOR_ACCOUNT
        // network response for this config_code.
        const trimmed: string | undefined = record?.text_value?.trim();
        const isInactive = record?.status?.toLowerCase() === 'in-active';
        const isEnabled = !isInactive && !!trimmed && trimmed.toLowerCase() !== 'no' && this.isHttpsUrl(trimmed);

        this.chatBaseUrl = isEnabled ? trimmed! : null;
        this.visible = isEnabled;
        this.cdr.markForCheck();
      },
      error: () => {
        this.visible = false;
        this.cdr.markForCheck();
      },
    });
  }

  // Config value is server-controlled but not fully trusted — only an https://
  // URL may reach bypassSecurityTrustResourceUrl (blocks javascript:/data: etc).
  private isHttpsUrl(url: string): boolean {
    try {
      return new URL(url).protocol === 'https:';
    } catch {
      return false;
    }
  }

  get safeUrl(): SafeResourceUrl {
    if (this.safeChatUrlFor !== this.chatUrl) {
      this.safeChatUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chatUrl!);
      this.safeChatUrlFor = this.chatUrl;
    }
    return this.safeChatUrl!;
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    if (!this.loaded) {
      this.requestAuthCode();
    }
  }

  close(): void {
    this.isOpen = false;
  }

  retry(): void {
    this.requestAuthCode();
  }

  private requestAuthCode(): void {
    const uhId = this.baseService.getpatientUhid();
    if (!this.chatBaseUrl || !uhId) {
      this.authError = true;
      this.cdr.markForCheck();
      return;
    }

    this.authorizing = true;
    this.authError = false;
    this.cdr.markForCheck();

    this.baseService
      .add({ uhId }, AUTH_CODE_RESOURCE, true, AUTH_CODE_APPLICATION)
      .subscribe({
        next: (res: any) => {
          this.authorizing = false;
          const code = res?.errCode === 0 ? res?.resource?.[0]?.code : undefined;
          if (!code) {
            this.authError = true;
            this.cdr.markForCheck();
            return;
          }

          const separator = this.chatBaseUrl!.includes('?') ? '&' : '?';
          this.chatUrl = `${this.chatBaseUrl}${separator}code=${encodeURIComponent(code)}`;
          this.loaded = true;
          this.isOpen = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.authorizing = false;
          this.authError = true;
          this.cdr.markForCheck();
        },
      });
  }
}
